<template>
  <div class="bar-chart-container container" :id="`bar-chart-container-${chartId}`" :class="{ 'separator' : chartData.separator }">
    <div v-if="!chartData['hide-header']" class="chart-header">
      <h1>{{ chartData.title }}</h1>
      <h2 v-if="chartData.subtitle" v-html="chartData.subtitle" />
      <div class="keys">
        <div
          v-for="(key, index) in chartData.keys"
          :style="`width: calc(${100 / chartData.keys.length}% - ${(chartData.keys.length - 1) * 5}px); background-color: ${colors[index]}`"
          :key="`key-${chartId}-${index}`"
          class="key"
          :class="[{ dark: index > 3 }, { 'high-number': chartData.keys.length > 3 }]"
        >
          <span class="contents">
            <span>{{ key }}</span>
            <span v-if="chartData.totals && chartData.totals[index]">{{ `: ${chartData.totals[index]}` }}</span>
          </span>
        </div>
      </div>
    </div>
    <svg :id="`bar-chart-${chartId}`"></svg>
  </div>
</template>

<script>
import * as d3 from 'd3'
import axios from 'axios'
export default {
  props: {
    dataUrl: {
      type: String,
      default: ''
    },
    chartId: {
      type: String,
      default: ''
    }
  },
  data () {
    return {
      chartData: {},
      colors: [
        '#c5dffc',
        '#92c5f5',
        '#5ba6eb',
        '#2885dd',
        '#0d69c9',
        '#044bad',
        '#032e8e',
        '#041774'
      ],
      groupPadding: 0.13
    }
  },

  computed: {
    chartDataFormatted () {
      const formatted = this.chartData.data
      formatted.forEach((group) => {
        group.values.forEach((val, index) => {
          group[this.chartData.keys[index]] = val
        })
      })
      return formatted
    },
    barThickness () {
      return this.chartData && this.chartData.barThickness ? this.chartData.barThickness : 30
    }
  },

  mounted () {
    this.getChartData()

    window.addEventListener('resize', () => {
      this.generateChartGroups()
    })
  },

  beforeDestroy () {
    window.removeEventListener('resize', () => {
      this.generateChartGroups()
    })
  },

  methods: {
    /**
     * Used to retrieve chart data from URL
     */
    getChartData () {
      axios.get(this.dataUrl).then((response) => {
        // Format received data
        this.chartData = response.data
        // console.log(this.chartData)
        this.generateChartGroups()
      }).catch((err) => {
        console.log(err)
      })
    },

    getGraphicHeight () {
      if (!this.chartData.data) return 0
      // First get height based on amount of data
      const initHeight = this.chartData.data.length * this.chartData.keys.length * this.barThickness
      // Then adjust according to padding for number of groups
      const adjust = initHeight * this.groupPadding
      return initHeight + adjust
    },

    generateChartGroups () {
      /**
       * Based on: https://observablehq.com/@d3/grouped-bar-chart
       */

      d3.select(`#bar-chart-${this.chartId}`).html('')

      const mobileMode = window.innerWidth < 1200 ? true : false

      const labelMax = 200

      const graphic = d3.select(`#bar-chart-${this.chartId}`)
      const graphicElement = document.getElementById(`bar-chart-container-${this.chartId}`)
      const styles = window.getComputedStyle(graphicElement)
      const graphicPadding = parseFloat(styles.paddingLeft) + parseFloat(styles.paddingRight)
      const margin = 0 // Margin around entire graph
      const dataTextMarginLeft = 10
      const axisTextMargin = 10
      let width =  graphicElement.clientWidth - graphicPadding - 45
      const labelMaxMobile = width * 0.333
      mobileMode ? width -= labelMaxMobile : ''
      const height = this.getGraphicHeight()

      document.getElementById(`bar-chart-${this.chartId}`).setAttribute('height', height + 'px')

      let y0 = d3.scaleBand()
        .domain(this.chartDataFormatted.map(d => d['title']))
        .rangeRound([margin, height - margin * 2])
        .paddingInner(this.groupPadding) // Padding between groups

      let y1 = d3.scaleBand()
        .domain(this.chartData.keys)
        .rangeRound([0, y0.bandwidth()])
        .padding(this.chartData && this.chartData.groupPadding ? this.chartData.groupPadding : 0) // Padding between bars in group

      let x = d3.scaleLinear()
        .domain([0, d3.max(this.chartDataFormatted, d => d3.max(this.chartData.keys, key => d[key]))]).nice()
        .rangeRound([width, margin])

      // Color sequence
      let color = d3.scaleOrdinal()
        .range(this.colors)

      // Add group labels
      let xAxis = g => g
        .call(d3.axisLeft(y0).tickSize(0).tickFormat(() => { return '' }))
        .attr('transform', `translate(${-axisTextMargin}, 0)`)
        .call(g => g.select('.domain').remove())

      graphic.append('g')
        // First add each group
        .selectAll('g')
        .data(this.chartDataFormatted)
        .join('g')
          .attr('transform', d => `translate(${mobileMode ? labelMaxMobile : 0}, ${y0(d['title'])})`)
        .selectAll('rect')
        // Then add rects for each group
        .data(d => this.chartData.keys.map(key => ({ key, value: d[key]})))
        .join('rect')
          .attr('y', d => y1(d.key))
          .attr('x', 0) // .attr('x', d => x(d.value)) (right aligned)
          .attr('height', y1.bandwidth())
          .attr('width', d => x(0) - x(d.value))
          .attr('fill', d => color(d.key))
        
      // Add text at end of each bar
      graphic.append('g')
        .selectAll('g')
        .data(this.chartDataFormatted)
        .join('g')
          .attr('transform', d => `translate(${mobileMode ? labelMaxMobile : 0}, ${y0(d['title'])})`)
        .selectAll('text')
        .data(d => this.chartData.keys.map(key => ({key, value: d[key]})))
        .join('text')
          .attr('class', 'value')
          .attr('y', d => y1(d.key) + y1.bandwidth() * 0.5)
          .attr('x', d => x(0) - x(d.value) + dataTextMarginLeft)
          .text(d => d.value + (this.chartData['data-suffix'] || '') || '')
          .attr('alignment-baseline', 'middle')
      
      // Axis text
      graphic.append('g')
        .attr('class', 'axis')
        .call(xAxis)
        .selectAll('g')
        .append('svg:foreignObject') // Use foreignObject for <p> with word wrapping
        .attr('width', (mobileMode ? labelMaxMobile : labelMax) - (mobileMode ? axisTextMargin : 0))
        .attr('height', 38)
        .attr('x', mobileMode ? 10 : -labelMax)
        .attr('y', 0)
        .append('xhtml:p')
        .html((d) => { return d })
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/assets/style/_variables';
@import '@/assets/style/_articles';
.article-container .bar-chart-container {
  position: relative;
  &.separator {
    &::after {
      content: '';
      position: absolute;
      left: $space-s;
      bottom: -$space-s-article;
      width: calc(100% - #{$space-s * 2});
      border-bottom: solid 1px rgba(0, 0, 0, 0.5);
    }
  }
  .chart-header {
    h1 {
      @include media($bp-tablet) {
        font-size: 36px;
      }
      @include media($bp-phone-l) {
        font-size: 24px;
      }
    }
    h2 {
      @include media($bp-phone-l) {
        font-size: 22px;
      }
    }
  }
  svg {
    width: 100%;
    overflow: visible;
  }
  ::v-deep {
    .axis {
      font-size: inherit;
      font-family: inherit;
      
      text {
        max-width: 200px;
      }

      .tick {
        overflow: visible;
        foreignObject {
          overflow: visible;
        }
        p {
          // fixed, right fixes positioning issue on Safari for some reason.
          position: fixed;
          right: 0;
          text-align: right;
          line-height: 1.1em;
          transform: translateY(calc(-50% - 2px));

          @include media(1200px) {
            font-size: 14px;
          }
        }
      }
    }
    .value {
      font-weight: 700;
    }
  }
}
</style>
